import React, { useMemo } from 'react'
import {
  Vertical,
  Horizontal,
  Texto,
  BBDTag,
  MaterialIcon,
  GraviGrid,
  addCommasToNumber,
} from '@gravitate-js/excalibrr'
import { useFormatter } from '@api/useFormatter'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import type { components } from '../../../../../types/api/v1'
import { useMatchSummaryData, type MatchSummaryRow } from '../hooks/useMatchSummaryData'
import {
  createMatchSummaryColumnDefs,
  type ConsistentCurrencyFormatters,
} from '../columnDefs/detailColumnDefs/columns/matchSummaryColumns'
import { calculateMatchSummaryPrecisions } from '../utils/precisionCalculator'
import {
  formatCurrencyConsistent,
  formatVarianceConsistent,
  formatPerUnitConsistent,
} from '../columnDefs/detailColumnDefs/formatters'

type BolCandidate = components['schemas']['BolCandidate']
type InvoiceCandidate = components['schemas']['InvoiceCandidate']

interface MatchSummaryGridProps {
  matchDetails: MatchDetailResponse
  pendingItems?: (InvoiceCandidate | BolCandidate)[]
  onPerUnitClick?: (priceId: string) => void
  onMappingRefresh?: () => void
}

export function MatchSummaryGrid({
  matchDetails,
  pendingItems = [],
  onPerUnitClick,
  onMappingRefresh,
}: MatchSummaryGridProps) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  // Use the existing hook for match summary data transformation
  const {
    matchSummaryData,
    matchSummaryGridHeight: hookGridHeight,
    totalVarianceWithPending,
  } = useMatchSummaryData(matchDetails, pendingItems)

  // Calculate precision requirements based on the actual data
  const precisionData = useMemo(() => {
    return calculateMatchSummaryPrecisions(matchSummaryData)
  }, [matchSummaryData])

  // Create dynamic currency formatters with precision support
  const currencyFormatters: ConsistentCurrencyFormatters = useMemo(
    () => ({
      formatCurrency: (amount?: number) => {
        if (amount === null || amount === undefined) return `${currencySymbol}0.00`
        if (amount === 0) return `${currencySymbol}0.00`
        const sign = amount < 0 ? '-' : ''
        return `${sign}${currencySymbol}${addCommasToNumber(Math.abs(amount), 2)}`
      },
      formatVariance: (value: number, showSign?: boolean) => {
        if (value === 0) return `${currencySymbol}0.00`
        const sign = showSign && value !== 0 ? (value > 0 ? '+' : '-') : ''
        return `${sign}${currencySymbol}${addCommasToNumber(Math.abs(value), 2)}`
      },
      formatPerUnit: (amount?: number) => {
        if (amount === null || amount === undefined) return `${currencySymbol}0.0000`
        if (amount === 0) return `${currencySymbol}0.0000`
        const sign = amount < 0 ? '-' : ''
        return `${sign}${currencySymbol}${Math.abs(amount).toFixed(4)}`
      },
      // Consistent formatters
      formatCurrencyConsistent: (amount?: number, precision?: number) => {
        return formatCurrencyConsistent(amount || 0, precision || precisionData.precisions.currency, currencySymbol)
      },
      formatVarianceConsistent: (value: number, precision?: number, showSign?: boolean) => {
        return formatVarianceConsistent(
          value,
          precision || precisionData.precisions.currency,
          currencySymbol,
          showSign || false
        )
      },
      formatPerUnitConsistent: (amount?: number, precision?: number) => {
        return formatPerUnitConsistent(amount, precision || precisionData.precisions.perUnit, currencySymbol, false)
      },
      currencySymbol,
    }),
    [currencySymbol, precisionData.precisions]
  )

  // Create column definitions with stable dependencies to prevent unnecessary column recreation
  // Only depend on primitive values to avoid triggering full column rebuild on timezone changes
  const matchSummaryColumns = useMemo(() => {
    return createMatchSummaryColumnDefs(
      onPerUnitClick,
      matchDetails.supplier_id,
      matchDetails.supplier_name,
      matchDetails.status,
      currencyFormatters,
      precisionData.precisions,
      onMappingRefresh
    )
  }, [
    onPerUnitClick,
    matchDetails.supplier_id,
    matchDetails.supplier_name,
    matchDetails.status,
    currencyFormatters,
    precisionData.precisions,
    onMappingRefresh,
  ])

  // Use the variance calculated by the hook (which handles original + pending properly)
  const totalVariance =
    totalVarianceWithPending !== undefined ? totalVarianceWithPending : matchDetails?.total_variance || 0
  const hasVariance = Math.abs(totalVariance) >= 0.01

  // If no data, show a simple message
  if (!matchSummaryData || matchSummaryData.length === 0) {
    return (
      <Vertical
        flex='0'
        style={{
          overflow: 'visible',
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Texto category='h4' weight='bold'>
          MATCH SUMMARY
        </Texto>
        <Texto>No match summary data available.</Texto>
      </Vertical>
    )
  }

  return (
    <Vertical flex='0' style={{ overflow: 'visible', background: '#f5f5f5' }}>
      <Vertical
        flex='0'
        style={{
          overflow: 'visible',
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Horizontal justifyContent='spaceBetween' verticalCenter>
          <Horizontal flex={1} gap={16} verticalCenter>
            <Texto category='h4' weight='bold' style={{ whiteSpace: 'nowrap' }}>
              MATCH SUMMARY
            </Texto>
            <BBDTag
              warning={hasVariance && Math.abs(totalVariance) <= 5000}
              error={hasVariance && Math.abs(totalVariance) > 5000}
              icon={
                <MaterialIcon icon={hasVariance ? 'flag' : 'check_circle'} style={{ fontSize: 14, paddingTop: 1 }} />
              }
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {hasVariance ? 'Has Variance' : 'No Variance'}
            </BBDTag>
          </Horizontal>
          <Horizontal flex='0' style={{ overflow: 'visible' }} gap={16} verticalCenter>
            <Texto category='p2' style={{ whiteSpace: 'nowrap' }}>
              Total Variance
            </Texto>
            <Texto
              category='h4'
              weight='bold'
              appearance={hasVariance ? (Math.abs(totalVariance) > 5000 ? 'error' : 'warning') : 'default'}
              style={{ whiteSpace: 'nowrap' }}
            >
              {totalVariance > 0 ? '+' : totalVariance < 0 ? '-' : ''}
              {currencySymbol}
              {addCommasToNumber(Math.abs(totalVariance), 2)}
            </Texto>
          </Horizontal>
        </Horizontal>
      </Vertical>

      <Vertical
        flex='0'
        style={{
          minHeight: `${hookGridHeight}px`,
          height: `${hookGridHeight}px`,
          overflow: 'hidden',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <GraviGrid
          columnDefs={matchSummaryColumns}
          rowData={matchSummaryData}
          sideBar={false}
          agPropOverrides={{
            suppressRowGroupHidesColumns: true,
            rowGroupPanelShow: 'never',
            suppressMenuHide: true,
            suppressMovableColumns: true,
            suppressDragLeaveHidesColumns: true,
            suppressColumnMoveAnimation: true,
            suppressHorizontalScroll: true,
            enableCellTextSelection: false,
            suppressColumnVirtualisation: true,
            headerHeight: 48,
            rowHeight: 30, // Restored from old version
            getRowId: (params) =>
              params.data?._gridRowId || params.data?.id || `match_summary_row_${params.node.rowIndex}`,
            alwaysShowVerticalScroll: false,
            onGridReady: (params) => {
              setTimeout(() => {
                const gridElement = params.api.getGridBodyElement()
                const viewportElement = gridElement?.querySelector('.ag-body-viewport')

                if (viewportElement) {
                  viewportElement.scrollTop = 0
                }

                params.api.ensureIndexVisible(0, 'top')
                params.api.refreshCells({ force: true })
              }, 100)
            },
            onFirstDataRendered: (params) => {
              const displayedRowCount = params.api.getDisplayedRowCount()

              for (let i = 0; i < Math.min(5, displayedRowCount); i++) {
                const rowNode = params.api.getDisplayedRowAtIndex(i)
              }

              const allRowNodes: {
                index: number | null
                data: MatchSummaryRow
                rowTop: number | null
                displayed: boolean
              }[] = []
              params.api.forEachNode((node) => {
                if (node.data) {
                  allRowNodes.push({
                    index: node.rowIndex,
                    data: node.data,
                    rowTop: node.rowTop,
                    displayed: node.displayed,
                  })
                }
              })

              params.api.resetRowHeights()
              params.api.onRowHeightChanged()
            },
            getRowHeight: (params: { data?: { type?: string } }) => {
              const row = params.data
              if (row && row.type === 'total') {
                return 40 // Restored from old version
              }
              return 30 // Restored from old version
            },
            getRowStyle: (params: { data?: { type?: string; _isPending?: boolean } }) => {
              const row = params.data
              if (!row) return undefined

              // Pending rows get a light blue background
              if (row._isPending) {
                return {
                  background: '#e6f7ff',
                  fontStyle: 'italic',
                  opacity: '0.8',
                } as any
              }

              if (row.type === 'comparison' || row.type === 'total') {
                return {
                  background: 'var(--bg-2)',
                  fontWeight: 'bold',
                } as any
              }

              return undefined
            },
          }}
          style={{ height: '100%', width: '100%' }}
        />
      </Vertical>
    </Vertical>
  )
}
